import { assert, cx, useTranslations } from "@jugl-web/utils";
import { DPMonth, useDatePickerContext } from "@rehookify/datepicker";
import { FC, MouseEvent, useState } from "react";
import { Button } from "../../../../primitives";
import { PickerBaseProps } from "../../types";

interface MonthPickerProps extends PickerBaseProps {
  hasNavigation?: boolean;
}

export const MonthPicker: FC<MonthPickerProps> = ({
  onViewChange,
  hasNavigation = true,
}) => {
  const { t } = useTranslations();

  const {
    data: { months, calendars },
    propGetters: { monthButton },
  } = useDatePickerContext();

  const { month, year } = calendars[0];

  const [internalMonth, setInternalMonth] = useState(() => {
    const matchingMonth = months.find((dpMonth) => dpMonth.month === month);

    if (matchingMonth && !matchingMonth.disabled) {
      return matchingMonth;
    }

    const firstAvailableMonth = months.find((dpMonth) => !dpMonth.disabled);

    return firstAvailableMonth;
  });

  const handleMonthSelect = (
    event: MouseEvent<HTMLButtonElement>,
    dpMonth: DPMonth
  ) => {
    if (dpMonth.disabled) {
      return;
    }

    if (!hasNavigation) {
      monthButton(dpMonth).onClick?.(event);
      onViewChange("dayPicker");
    } else {
      setInternalMonth(dpMonth);
    }
  };

  const handleSubmit = (event: MouseEvent<HTMLButtonElement>) => {
    assert(!!internalMonth, "Month must be selected");
    monthButton(internalMonth).onClick?.(event);
    onViewChange("dayPicker");
  };

  return (
    <>
      <div className="flex h-[42px] items-center justify-between rounded-lg bg-grey-100 px-6">
        <span className="text-[13px] font-medium text-grey-800">
          {t({
            id: "date-picker-component.select-month",
            defaultMessage: "Select Month",
          })}
        </span>
        <span className="text-[18px] font-bold text-dark-800">
          {internalMonth?.month}
        </span>
      </div>
      <div className="mt-6 grid grid-cols-3 gap-x-1.5 gap-y-6">
        {months.map((dpMonth) => {
          const isSelected = internalMonth?.$date === dpMonth.$date;

          return (
            <button
              key={dpMonth.month + year}
              type="button"
              className={cx(
                "h-11 cursor-pointer rounded-[32px] border-none font-[Roboto] text-sm transition-colors",
                "bg-grey-100 font-normal text-[#19181A] hover:bg-grey-200",
                dpMonth.now &&
                  "bg-primary-50 font-semibold text-primary hover:bg-primary-50",
                dpMonth.disabled &&
                  "cursor-not-allowed bg-grey-100 font-normal text-grey-500 hover:bg-grey-100",
                isSelected &&
                  "bg-primary font-semibold text-white hover:bg-primary"
              )}
              onClick={(event) => handleMonthSelect(event, dpMonth)}
            >
              {dpMonth.month}
            </button>
          );
        })}
      </div>
      {hasNavigation && (
        <div className="mt-8 flex gap-4">
          <Button
            variant="tertiary"
            className="flex-1"
            onClick={() => onViewChange("dayPicker")}
          >
            {t({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
          <Button
            className="flex-1"
            isDisabled={internalMonth === undefined}
            onClick={handleSubmit}
          >
            {t({ id: "common.select", defaultMessage: "Select" })}
          </Button>
        </div>
      )}
    </>
  );
};
